import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Iconify from '../common/iconify.component';
import ListData from '../table-elements/list-data.component';
import {
  LISTING_ACTIONS,
  LISTING_COLUMNS,
} from '../../config/module-configs/categories.config';
import {
  deleteCategoryById,
  getDataList,
  toggleStatus,
} from '../../services/content.service';
import { ACTIONS, CONTENT_ALIAS } from '../../config/const.config';
import AddEditCategories from './add-edit-categories.component';
import ViewCategories from './view-categories.component';
import ConfirmPopup from '../common/confirm-popup.component';
import SnackbarInfo from '../common/snackbar-info.component';
import ImagePreview from '../content/image-preview.component';
import ROUTE_PATH from '../../config/routes.config';
import SearchList from '../table-elements/search-list.component';
import UploadAssets from '../content/upload-assets.component';
import editUrl from '../../utils/url-edit.util';

const ListCategories = ({
  categoryTypeSlug,
  categoryTypeName,
  categoryTypeId,
}) => {
  const columns = LISTING_COLUMNS;
  const actions = LISTING_ACTIONS;
  const navigate = useNavigate();
  const searchCategoriesRef = useRef(null);
  const [isConfirmationPromptOpen, setIsConfirmationPromptOpen] =
    useState(false);
  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 10,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    q: null,
    error: false,
    loading: true,
    searchValue: '',
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [imagePreview, setImagePreview] = useState({
    show: false,
    imageUrl: '',
    title: '',
  });

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleActionSelect = (selectedAction, selectedData) => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    if (selectedAction === ACTIONS.ASSIGN_CONTENT.value) {
      const url = editUrl(ROUTE_PATH.CATEGORY_CONTENT, selectedData.id);
      navigate(url);
    }
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };

  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });

    setImagePreview({
      ...imagePreview,
      show: false,
    });
  };

  const handleActionSuccess = (message = '') => {
    if (message) {
      setSnackbarInfo({
        ...snackbarInfo,
        show: true,
        type: 'success',
        message,
      });
    }

    handleActionCancel();
    handleRefreshData();
  };

  const handleDeleteData = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    deleteCategoryById(doAction.data.id)
      .then(() => {
        handleActionSuccess(
          `${CONTENT_ALIAS.CATEGORY_SINGULAR} deleted successfully.`
        );
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        handleActionCancel();
      });
  };

  const handleImagePreview = (rowData) => {
    setImagePreview({
      ...imagePreview,
      show: true,
      imageUrl: rowData.bannerImageUrl,
      title: rowData.title,
    });
  };

  const dataListAPICall = (newOptions = {}) => {
    let uri = 'categories';
    uri += `?page=${newOptions?.page || options.page + 1}`;
    uri += `&perPage=${options.rowsPerPage}`;
    if (options.sortBy && options.sortOrder) {
      uri += `&sortBy=${options.sortBy}`;
      uri += `&sortOrder=${options.sortOrder}`;
    }
    if (options.q) {
      uri += `&q=${options.q}`;
    } else if (searchCategoriesRef.current?.value) {
      uri += `&q=${searchCategoriesRef.current.value}`;
    }

    setOptions({ ...options, loading: true, searchValue: '' });

    getDataList(uri)
      .then((res) => {
        let serialRow;
        if (res?.data?.rows) {
          serialRow = res.data.rows;
          serialRow = serialRow.map((tr, count) => ({
            ...tr,
            serial: options.page * options.rowsPerPage + count + 1,
          }));
        }

        setOptions({
          ...options,
          loading: false,
          totalRows: res?.data?.totalRows || 0,
          rows: serialRow || [],
          q: null,
          error: false,
        });
      })
      .catch(() => {
        setOptions({
          ...options,
          loading: false,
          page: 0,
          totalRows: 0,
          rows: [],
          q: null,
          error: true,
        });
      });
  };

  const handleToggleStatus = (r, value, updateValueFor) => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      id: r?.id,
      status: value,
      type: 'category',
      updateValueFor,
    };

    let successToggleMessage = '';
    if (payload.updateValueFor === 'status') successToggleMessage = '';
    else if (payload.updateValueFor === 'showOnHome')
      successToggleMessage = 'Show On Home';
    else if (payload.updateValueFor === 'isFeatured')
      successToggleMessage = 'Is Featured';
    else successToggleMessage = 'Is Playlist';

    toggleStatus(payload)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: `${successToggleMessage} Status updated.`,
        });

        if (
          payload.updateValueFor === 'showOnHome' ||
          payload.updateValueFor === 'isPlaylist' ||
          payload.updateValueFor === 'isFeatured'
        ) {
          handleRefreshData();
        }
      })
      .catch((e) => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message: e.response?.data?.message,
        });
      });
  };

  const handleSearchChange = (e) => {
    setOptions({
      ...options,
      searchValue: e.target.value || '',
    });
  };

  const handleSearchCategories = () => {
    setOptions({
      ...options,
      q: searchCategoriesRef.current.value,
      reloadCounter: options.reloadCounter + 1,
      page: 0,
    });
  };

  const handleClearSearch = () => {
    setOptions({
      ...options,
      searchValue: '',
      q: null,
      reloadCounter: options.reloadCounter + 1,
    });
    searchCategoriesRef.current.value = '';
  };

  useEffect(() => {
    const newOptions = options;
    newOptions.page = 0;
    setOptions({
      ...options,
      page: 0,
    });

    if (searchCategoriesRef.current?.value) {
      searchCategoriesRef.current.value = null;
    }
    dataListAPICall(newOptions);
  }, [categoryTypeSlug]);

  useEffect(() => {
    dataListAPICall();
  }, [options.reloadCounter]);

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ px: 1, py: 2 }}
      >
        <div>
          <Button
            color="primary"
            variant="contained"
            startIcon={<Iconify icon="ic:round-add" />}
            onClick={() => handleActionSelect(ACTIONS.CREATE.value, null)}
          >
            Create New Category
          </Button>

          <SearchList
            label={CONTENT_ALIAS.CATEGORY_PLURAL}
            handleSearch={handleSearchCategories}
            inputRef={searchCategoriesRef}
            onChange={handleSearchChange}
            options={options}
            clearSearch={handleClearSearch}
            loading={options.loading}
          />
        </div>
        <div>
          <Button
            color="inherit"
            startIcon={<Iconify icon="ic:twotone-refresh" />}
            onClick={handleRefreshData}
            disabled={options.loading}
          >
            Refresh
          </Button>
        </div>
      </Stack>

      <Card>
        <ListData
          columns={columns}
          rows={options.rows}
          page={options.page}
          rowsPerPage={options.rowsPerPage}
          totalRows={options.totalRows}
          loading={options.loading}
          actions={actions}
          error={options.error}
          sortBy={options.sortBy}
          sortOrder={options.sortOrder}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortingChange}
          onAction={handleActionSelect}
          handleImagePreview={handleImagePreview}
          handleToggleStatus={handleToggleStatus}
        />
      </Card>

      {doAction.action === ACTIONS.CREATE.value && (
        <AddEditCategories
          title={`Add New ${CONTENT_ALIAS.CATEGORY_SINGULAR}`}
          dataId={0}
          categoryTypeName={categoryTypeName}
          categoryTypeId={categoryTypeId}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
        />
      )}

      {doAction.action === ACTIONS.EDIT.value && (
        <AddEditCategories
          title={`Edit ${CONTENT_ALIAS.CATEGORY_SINGULAR} Details - ${doAction.data.title}`}
          dataId={doAction.data.id}
          categoryTypeName={categoryTypeName}
          categoryTypeId={categoryTypeId}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
        />
      )}

      {doAction.action === ACTIONS.VIEW.value && (
        <ViewCategories
          title={`${CONTENT_ALIAS.CATEGORY_SINGULAR} Details - ${doAction.data.title}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
        />
      )}
      {doAction.action === ACTIONS.UPLOAD_ASSETS.value && (
        <UploadAssets
          title={`Upload Assets - ${doAction.data.title}`}
          dataId={doAction.data.id}
          dataTitle={doAction.data.title}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
          from="category"
        />
      )}

      {doAction.action === ACTIONS.DELETE.value && (
        <ConfirmPopup
          title={`Delete ${CONTENT_ALIAS.CATEGORY_SINGULAR}`}
          message={`Do you want to delete ${doAction.data.title} ${CONTENT_ALIAS.CATEGORY_SINGULAR}? It will remove all the content(videos/series/shows) assign to this category. You can not undo this action!`}
          onCancel={handleActionCancel}
          onSuccess={handleDeleteData}
        />
      )}

      {imagePreview.show && (
        <ImagePreview
          title={`Banner Image - ${imagePreview.title}`}
          onCancel={handleActionCancel}
          imageUrl={imagePreview.imageUrl}
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
      {isConfirmationPromptOpen && (
        <ConfirmPopup
          title="Confirm Action"
          message="Are you sure, You want to proceed? It will remove all the videos assign to this category"
          onCancel={() => setIsConfirmationPromptOpen(false)}
          onSuccess={() => {
            handleToggleStatus();
            setIsConfirmationPromptOpen(false);
          }}
          cancelButtonText="No"
          successButtonText="Yes"
        />
      )}
    </>
  );
};

ListCategories.propTypes = {
  categoryTypeSlug: PropTypes.string.isRequired,
  categoryTypeName: PropTypes.string.isRequired,
  categoryTypeId: PropTypes.number.isRequired,
};

export default ListCategories;
